import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import ProductMockup from "./ProductMockup";

const ease = [0.22, 1, 0.36, 1] as const;

type Stage = 0 | 1 | 2 | 3 | 4;
const STAGES = [
  { key: "monitor", label: "Monitor" },
  { key: "detect", label: "Detect" },
  { key: "investigate", label: "Investigate" },
  { key: "decide", label: "Decide" },
  { key: "resolve", label: "Resolve" },
] as const;

// Stage durations - approximately 9s total walkthrough
const STAGE_DELAYS = [0, 1800, 3600, 6200, 8600];

const bars = [38, 52, 47, 65, 58, 72, 80, 76, 88, 95];
const trendPath = "M 0 70 L 25 62 L 50 65 L 75 50 L 100 52 L 125 38 L 150 35 L 175 22 L 200 18";

type AlertLevel = "warn" | "ok" | "resolved";
interface AlertItem {
  id: string;
  label: string;
  level: AlertLevel;
}

const initialAlerts: AlertItem[] = [
  { id: "drift", label: "Drift detected · Sepsis-risk v1.7", level: "warn" },
  { id: "approve", label: "Workflow approved · Documentation-support v0.9", level: "ok" },
  { id: "audit", label: "Audit export · Q3 ready", level: "ok" },
];

const resolvedEntry: AlertItem = {
  id: "policy",
  label: "Policy updated · Sepsis-risk v1.7.1",
  level: "ok",
};

const userMsg1 = "How many governance events are unresolved past SLA?";
const agentMsg1 = "3 events across 2 workflows are past SLA - oldest is 14 hours overdue.";
const userMsg2 = "Which workflows are responsible for the most overdue events?";
const agentMsg2 = "Sepsis-risk (2 events) and documentation-support (1 event). Recommend assigning clinical and governance review.";

const actionChips = ["Assign governance review"];

interface CounterProps {
  to: number;
  prefix?: string;
  suffix?: string;
  format?: (n: number) => string;
  start: boolean;
  delay: number;
}

const Counter = ({ to, prefix = "", suffix = "", format, start, delay }: CounterProps) => {
  const reduce = useReducedMotion();
  const [val, setVal] = useState(reduce ? to : 0);

  useEffect(() => {
    if (!start || reduce) return;
    const startTime = performance.now() + delay * 1000;
    const duration = 1200;
    let raf: number;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - startTime) / duration));
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(eased * to);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, to, delay, reduce]);

  const display = format ? format(val) : Math.round(val).toLocaleString();
  return (
    <span>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

// Typewriter hook
const useTyped = (text: string, start: boolean, speed = 25) => {
  const reduce = useReducedMotion();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) {
      setN(0);
      return;
    }
    if (reduce) {
      setN(text.length);
      return;
    }
    let i = 0;
    setN(0);
    const id = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= text.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [start, text, speed, reduce]);
  return text.slice(0, n);
};

const TypingDots = () => (
  <span className="inline-flex items-center gap-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-1 w-1 rounded-full bg-muted-foreground/70"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </span>
);

const GovernMockup = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "0px 0px -10% 0px" });
  const reduce = useReducedMotion();
  const [stage, setStage] = useState<Stage>(reduce ? 4 : 0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setStage(4);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    STAGE_DELAYS.forEach((delay, i) => {
      timers.push(setTimeout(() => setStage(i as Stage), delay));
    });
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce]);

  const driftActive = stage >= 1 && stage < 4;
  const driftResolved = stage >= 4;
  const showChat = stage >= 2;

  // Stage 3 -> user msg 1 visible immediately, agent reply types after delay
  const showUser1 = stage >= 2;
  const showAgent1Typing = stage >= 2;
  const showAgent1Text = stage >= 2;
  const agent1Typed = useTyped(agentMsg1, showAgent1Text, 22);
  const agent1Done = agent1Typed.length === agentMsg1.length;

  const showUser2 = stage >= 3;
  const showAgent2Text = stage >= 3;
  const agent2Typed = useTyped(agentMsg2, showAgent2Text, 22);
  const agent2Done = agent2Typed.length === agentMsg2.length;

  const showChips = stage >= 3 && agent2Done;
  const chipSelected = stage >= 4;

  // Build alerts list (resolved entry prepended at stage 4)
  const alerts: AlertItem[] = driftResolved
    ? [resolvedEntry, ...initialAlerts.map((a) => (a.id === "drift" ? { ...a, level: "resolved" as AlertLevel } : a))]
    : initialAlerts.map((a) =>
        a.id === "drift" && driftActive ? { ...a, level: "warn" as AlertLevel } : a,
      );

  const chatBody = (
    <div className="space-y-2">
      {showUser1 && (
        <motion.div
          initial={{ opacity: 0, x: reduce ? 0 : 6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease }}
          className="flex justify-end"
        >
          <div className="max-w-[80%] rounded-md bg-muted px-2.5 py-1.5 text-[10px] text-foreground/80">
            {userMsg1}
          </div>
        </motion.div>
      )}
      {showAgent1Typing && (
        <motion.div
          initial={{ opacity: 0, x: reduce ? 0 : -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease, delay: 0.4 }}
          className="flex justify-start"
        >
          <div className="max-w-[85%] rounded-md border-l-2 border-primary bg-background px-2.5 py-1.5 text-[10px] text-foreground/90">
            {agent1Typed.length === 0 ? <TypingDots /> : agent1Typed}
            {!agent1Done && agent1Typed.length > 0 && (
              <span className="inline-block w-px h-3 bg-primary align-middle ml-0.5 animate-pulse" />
            )}
          </div>
        </motion.div>
      )}
      {showUser2 && (
        <motion.div
          initial={{ opacity: 0, x: reduce ? 0 : 6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease }}
          className="flex justify-end"
        >
          <div className="max-w-[80%] rounded-md bg-muted px-2.5 py-1.5 text-[10px] text-foreground/80">
            {userMsg2}
          </div>
        </motion.div>
      )}
      {showAgent2Text && (
        <motion.div
          initial={{ opacity: 0, x: reduce ? 0 : -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease, delay: 0.4 }}
          className="flex justify-start"
        >
          <div className="max-w-[85%] rounded-md border-l-2 border-primary bg-background px-2.5 py-1.5 text-[10px] text-foreground/90">
            {agent2Typed.length === 0 ? <TypingDots /> : agent2Typed}
            {!agent2Done && agent2Typed.length > 0 && (
              <span className="inline-block w-px h-3 bg-primary align-middle ml-0.5 animate-pulse" />
            )}
          </div>
        </motion.div>
      )}
      <AnimatePresence>
        {showChips && (
          <motion.div
            key="chips"
            initial={{ opacity: 0, y: reduce ? 0 : 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease }}
            className="flex flex-wrap gap-1.5 pt-1"
          >
            {actionChips.map((chip, i) => {
              const isPrimary = i === 0;
              const selected = isPrimary && chipSelected;
              return (
                <motion.span
                  key={chip}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, ease, delay: i * 0.08 }}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wider ${
                    selected
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground"
                  }`}
                >
                  {selected && (
                    <motion.svg
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3, ease, delay: 0.1 }}
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M1.5 4.5L3 6L6.5 2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </motion.svg>
                  )}
                  {chip}
                </motion.span>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const chatHeader = (
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <p className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              Governance review
            </p>
          </div>
          <span className="text-muted-foreground/60 text-xs leading-none">⋯</span>
        </div>
  );

  return (
    <div className="relative">
    <div className="md:mr-[180px] lg:mr-[240px]">
    <ProductMockup label="governance · operations dashboard" contentHeight="h-auto md:h-[560px]">
      {/* Stage indicator */}
      <div ref={ref} className="flex items-center gap-1.5 md:gap-3 border-b border-border bg-background/40 px-3 py-2 overflow-x-auto">
        {STAGES.map((s, i) => {
          const active = stage === i;
          const done = stage > i;
          return (
            <div key={s.key} className="flex items-center gap-1.5 md:gap-3 shrink-0">
              <div className="flex items-center gap-1.5">
                <motion.span
                  aria-hidden
                  className={`h-1.5 w-1.5 rounded-full ${done || active ? "bg-primary" : "bg-foreground/20"}`}
                  animate={
                    active && !reduce
                      ? { scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }
                      : { scale: 1, opacity: 1 }
                  }
                  transition={{ duration: 1.4, ease, repeat: active ? Infinity : 0 }}
                />
                <span
                  className={`text-[8.5px] md:text-[9px] uppercase tracking-[0.14em] ${
                    active ? "text-primary" : done ? "text-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <span className={`h-px w-4 md:w-6 ${done ? "bg-primary/50" : "bg-border"}`} aria-hidden />
              )}
            </div>
          );
        })}
      </div>

      <div className="relative bg-background p-4 md:p-5 space-y-4">
        {/* KPI tiles */}
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {[
            { label: "Adoption", value: 87, suffix: "%", warn: true },
            { label: "Value · mo", value: 1240, suffix: " hrs", warn: false },
            { label: "ROI YTD", value: 2.4, prefix: "€", suffix: "M", format: (n: number) => n.toFixed(1), warn: false },
          ].map((kpi, i) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: reduce ? 0 : 6 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, ease, delay: 0.1 + i * 0.08 }}
              className="relative rounded-md border border-border bg-card p-2 md:p-3"
            >
              <p className="text-[8px] md:text-[9px] uppercase tracking-[0.12em] md:tracking-[0.14em] text-muted-foreground truncate">
                {kpi.label}
              </p>
              <p className="mt-1 md:mt-1.5 text-foreground text-base md:text-xl font-medium tabular-nums">
                <Counter
                  to={kpi.value}
                  prefix={kpi.prefix}
                  suffix={kpi.suffix}
                  format={kpi.format}
                  start={inView}
                  delay={0.3 + i * 0.1}
                />
              </p>
              <AnimatePresence>
                {kpi.warn && driftActive && (
                  <motion.span
                    key="warn"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[8px] font-bold flex items-center justify-center"
                    aria-label="Alert"
                  >
                    !
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* Bar chart */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, ease, delay: 0.4 }}
            className="md:col-span-8 rounded-md border border-border bg-card p-3"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
                Workflows monitored · 10 weeks
              </p>
              <p className="text-[9px] text-primary uppercase tracking-wider">+18% MoM</p>
            </div>
            <div className="flex items-end gap-1.5 h-[80px]">
              {bars.map((h, i) => (
                <motion.div
                  key={i}
                  className="flex-1 bg-gradient-to-t from-primary/70 to-primary/30 rounded-sm"
                  initial={{ height: reduce ? `${h}%` : "0%" }}
                  animate={inView ? { height: `${h}%` } : {}}
                  transition={{ duration: 0.7, ease, delay: 0.6 + i * 0.08 }}
                />
              ))}
            </div>
            <div className="relative mt-2 h-[14px]">
              <p className="absolute bottom-0 left-0 text-[8px] uppercase tracking-wider text-muted-foreground">
                Performance trend
              </p>
            </div>

          </motion.div>

          {/* Activity list */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, ease, delay: 0.5 }}
            className="md:col-span-4 rounded-md border border-border bg-card p-3"
          >
            <p className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground mb-3">
              Activity
            </p>
            <ul className="space-y-2.5">
              <AnimatePresence initial={false}>
                {alerts.map((a, i) => (
                  <motion.li
                    key={a.id}
                    initial={{ opacity: 0, x: reduce ? 0 : 4 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease, delay: a.id === "policy" ? 0 : 0.9 + i * 0.15 }}
                    className="flex items-start gap-2 text-[10px] leading-snug"
                  >
                    <motion.span
                      className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                      animate={{
                        backgroundColor:
                          a.level === "warn"
                            ? "hsl(var(--primary))"
                            : a.level === "resolved"
                              ? "hsl(var(--muted-foreground) / 0.5)"
                              : "hsl(var(--foreground) / 0.4)",
                        scale: a.level === "warn" && driftActive ? [1, 1.5, 1] : 1,
                      }}
                      transition={{
                        backgroundColor: { duration: 0.4 },
                        scale: { duration: 1.2, repeat: a.level === "warn" && driftActive ? Infinity : 0 },
                      }}
                    />
                    <span
                      className={`${
                        a.level === "resolved" ? "text-muted-foreground line-through" : "text-foreground/80"
                      }`}
                    >
                      {a.label}
                    </span>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </motion.div>
        </div>

        {/* Compliance & audit panel (full width; chat floats above) */}
        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : 6 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, ease, delay: 0.6 }}
          className="rounded-md border border-border bg-card p-3"
        >
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              Compliance &amp; audit
            </p>
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <p className="text-[8px] uppercase tracking-wider text-muted-foreground">
                EU AI Act · MDR
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5 divide-y divide-border border-y border-border">
              {[
                { label: "Audit events · 24h", value: "12,840", primary: false },
                { label: "Workflows monitored", value: "12", primary: false },
                { label: "Open incidents", value: "1", primary: true },
              ].map((s) => (
                <div key={s.label} className="flex items-center justify-between py-1.5">
                  <span className="text-[10px] text-foreground/70">{s.label}</span>
                  <motion.span
                    className={`text-[11px] font-medium tabular-nums ${
                      s.primary ? "text-primary" : "text-foreground"
                    }`}
                    animate={
                      s.primary && driftActive
                        ? { opacity: [1, 0.5, 1] }
                        : { opacity: 1 }
                    }
                    transition={{ duration: 1.4, repeat: s.primary && driftActive ? Infinity : 0 }}
                  >
                    {s.value}
                  </motion.span>
                </div>
              ))}
            </div>

            <div className="md:col-span-7">
              <p className="text-[8px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Evidence trail
              </p>
              <ul className="space-y-1.5">
                {[
                  { label: "Inference logged", time: "09:41", level: "info" },
                  { label: "Drift signal raised", time: "09:42", level: "warn" },
                  { label: "Threshold re-baselined", time: "09:44", level: "info" },
                  { label: "Clinical reviewer notified", time: "09:45", level: "info" },
                  { label: "Audit entry sealed", time: "09:46", level: "resolved" },
                ].map((e, i) => (
                  <motion.li
                    key={e.label}
                    initial={{ opacity: 0, x: reduce ? 0 : 4 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, ease, delay: 0.7 + i * 0.08 }}
                    className="flex items-center gap-2 text-[10px] leading-snug"
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                        e.level === "warn"
                          ? "bg-primary"
                          : e.level === "resolved"
                            ? "bg-muted-foreground/50"
                            : "bg-foreground/40"
                      }`}
                    />
                    <span
                      className={`flex-1 truncate ${
                        e.level === "resolved" ? "text-muted-foreground" : "text-foreground/80"
                      }`}
                    >
                      {e.label}
                    </span>
                    <span className="text-[9px] tabular-nums text-muted-foreground">
                      {e.time}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 pb-5 border-t border-border">
            <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[9px] uppercase tracking-wider text-foreground/70">
              Export evidence pack
            </span>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
              View full audit
            </span>
          </div>
        </motion.div>
      </div>
    </ProductMockup>
    </div>

    {/* Dim overlay to fade the dashboard behind the chat */}
    <AnimatePresence>
      {showChat && (
        <motion.div
          key="dim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease }}
          className="hidden sm:block absolute inset-0 z-20 bg-background/30 pointer-events-none rounded-lg"
          aria-hidden
        />
      )}
    </AnimatePresence>

    {/* Floating overlay chat — sits ON TOP of the dashboard */}
    <AnimatePresence>
      {showChat && (
        <motion.div
          key="floating-chat"
          initial={{ opacity: 0, y: reduce ? 0 : -8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease }}
          className="hidden sm:block absolute z-30 top-8 right-0 md:top-10 md:right-0 w-[380px] md:w-[460px] rounded-lg border border-border/80 bg-card ring-1 ring-foreground/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45),0_15px_30px_-10px_rgba(0,0,0,0.35)]"
        >
          <div className="p-4">
            {chatHeader}
            {chatBody}
          </div>
          {/* Input field */}
          <div className="border-t border-border p-3">
            <div className="rounded-md bg-muted/50 px-2.5 py-2 text-[11px] text-foreground/70 mb-2">
              <span className="text-primary font-medium">@deepc</span>{" "}
              flag sepsis-risk and documentation-support for clinical review
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground/70">
                <span className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[11px]">+</span>
                <span className="text-[10px]">Aa</span>
                <span className="text-[10px]">@</span>
                <span className="text-[10px]">/</span>
              </div>
              <button
                type="button"
                className="inline-flex items-center justify-center h-6 w-8 rounded-md bg-primary text-primary-foreground"
                aria-label="Send"
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L10 2L6 10L5 7L2 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Mobile fallback: inline chat below dashboard */}
    <AnimatePresence>
      {showChat && (
        <motion.div
          key="mobile-chat"
          initial={{ opacity: 0, y: reduce ? 0 : 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease }}
          className="sm:hidden mt-3 rounded-lg border border-border bg-card p-3"
        >
          {chatHeader}
          {chatBody}
        </motion.div>
      )}
    </AnimatePresence>
    </div>
  );
};

export default GovernMockup;
